import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

import {
  OnboardingContextActionType,
  OnboardingPayloadData,
  useOnboardingDispatch
} from '@/contexts/onboardingContext';
import useAPI from '@/pages/members/hooks/useAPI';
import OnboardingRepository from '@/repositories/members/OnboardingRepository';
import Spinner from '@/ui/members/components/Spinner';
import { loadRecaptcha } from '@/utils/recaptcha';

import { MOBILE_EVENT_PAGE_LOADED } from '../../types';

import { getInitialPayload } from './utils/getInitialPayload';

const Onboarding = () => {
  const navigate = useNavigate();
  const dispatch = useOnboardingDispatch();
  const repository = OnboardingRepository();

  const [enabled, setEnabled] = useState(true);
  const [queryParams, _] = useSearchParams();
  const { data, isLoading } = useAPI({
    queryKey: ['onboardingInitialData'],
    queryFn: async () => {
      return await repository.get({
        httpReferrerUrl: document.referrer || null,
        gtm_referrer: queryParams.get('gtm_referrer')
      });
    },
    enabled
  });

  useEffect(() => {
    if (data) {
      if (data.recaptchaSiteKey) {
        loadRecaptcha(data.recaptchaSiteKey);
      }
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ eventType: MOBILE_EVENT_PAGE_LOADED })
      );
      if (!data.initialized) {
        dispatch({
          type: OnboardingContextActionType.INIT,
          payload: getInitialPayload(data, queryParams)
        });
      } else {
        // flatten all data in each data.steps payloads into data property
        const flattenedData = Object.entries(data.steps).reduce((acc, [_, steps]) => {
          steps.forEach((step) => {
            Object.entries(step.payload || {}).forEach(([key, value]) => {
              acc[key] = value;
            });
          });
          return acc;
        }, {} as OnboardingPayloadData);
        dispatch({
          type: OnboardingContextActionType.SET_ONBOARDING_DATA,
          payload: flattenedData
        });

        if (data.completed) {
          navigate(`/onboarding/success`);
          setEnabled(false);
          return;
        }

        dispatch({
          type: OnboardingContextActionType.CONTINUE,
          payload: data
        });
      }
      setEnabled(false);
      navigate({
        pathname: `/onboarding/${data.version}/${data.step}`,
        search: window.location.search
      });
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const overflowClass = isMobile ? 'overflow-x-hidden' : '';

  return (
    <div className="flex flex-col pt-6 h-full min-h-screen">
      {isLoading ? (
        <Spinner className="m-auto" color="text-gray-500" size={20} visibleLabel={false} />
      ) : (
        <div className={`${overflowClass} mb-4 mx-auto py-2 px-3 xs:px-6 xs:w-full sm:w-110`}>
          <Outlet />
        </div>
      )}
    </div>
  );
};
export default Onboarding;
